import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import MuiListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Collapse from "@material-ui/core/Collapse";
import Tooltip from "@material-ui/core/Tooltip";
import useMenu from "hooks/useMenu";
import useUser from "hooks/useUser";
import RequestFormModal from "components/RequestFormModal/RequestFormModal";
import useModal from "hooks/useModal";
import { SHOW_SUBMIT_REQUEST } from "services/Http";
import MaterialSymbol from "components/MaterialSymbol/MaterialSymbol";
import { isAdminUiSkinEnabled } from "adminUiSkin";

const showSubmitRequest = SHOW_SUBMIT_REQUEST === "Y";

const useStyles = makeStyles((theme) => ({
    root: (props) => ({
        width: "100%",
        maxWidth: props.isShadcn ? "none" : 360,
        backgroundColor: "#333333",
        boxSizing: "border-box",
    }),
    white: {
        color: theme.palette.common.white,
    },
    nested: {
        paddingLeft: theme.spacing(4),
    },
}));

const ListItem = withStyles({
    root: {
        borderRadius: 5,
        margin: "5px 0px 5px 0px",
        "& span": {
            fontSize: "14px",
        },
        "&$selected": {
            backgroundColor: "#ef662f",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
        "&$selected:hover": {
            backgroundColor: "#ef662f",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
        "&:hover": {
            backgroundColor: "#474747",
            color: "white",
            "& .MuiListItemIcon-root": {
                color: "white",
            },
        },
    },
    selected: {},
})(MuiListItem);

function SidebarItemLabel({ name }) {
    const textRef = React.useRef(null);
    const [isTruncated, setIsTruncated] = React.useState(false);

    React.useEffect(() => {
        const node = textRef.current;
        if (!node) return;

        const checkTruncation = () => {
            setIsTruncated(node.scrollWidth > node.clientWidth);
        };

        checkTruncation();
        window.addEventListener("resize", checkTruncation);

        return () => {
            window.removeEventListener("resize", checkTruncation);
        };
    }, [name]);

    return (
        <Tooltip title={name} placement="right" arrow disableHoverListener={!isTruncated}>
            <ListItemText
                primary={name}
                primaryTypographyProps={{
                    ref: textRef,
                }}
            />
        </Tooltip>
    );
}

function CustomListItem(props) {
    const {
        classes,
        name,
        views = [],
        layout,
        path,
        createLinks = () => {},
        history,
        depth = 0,
    } = props;
    const [open, setOpen] = React.useState(false);

    const handleClick = () => {
        if (views.length > 0) setOpen(!open);
        else history.push(`${layout}${path}`);
    };

    const isSelected = history.location.pathname === `${layout}${path}`;

    return (
        <>
            <ListItem
                button
                onClick={handleClick}
                className={`custom-sidebar-item custom-sidebar-item-depth-${depth}`}
                selected={isSelected}
            >
                {/*<ListItemIcon style={{ minWidth: 35 }}>
                    <props.icon className={classes.white} />
                </ListItemIcon>*/}
                <SidebarItemLabel name={name} />
                {views.length > 0 ? (
                    <MaterialSymbol className="custom-sidebar-chevron">
                        {open ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                    </MaterialSymbol>
                ) : null}
            </ListItem>
            {views.length > 0 ? (
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    className={`custom-sidebar-collapse custom-sidebar-collapse-depth-${depth}`}
                >
                    <List component="div" disablePadding>
                        {createLinks(views, depth + 1)}
                    </List>
                </Collapse>
            ) : null}
        </>
    );
}

const ASSIGNED_ROLES_ONLY = true;

export default function CustomSidebar() {
    const isShadcn = isAdminUiSkinEnabled();
    const classes = useStyles({ isShadcn });
    const history = useHistory();
    const [user] = useUser();
    const { treeMenu = [] } = useMenu(ASSIGNED_ROLES_ONLY);
    const { open, handleOpenModal, handleCloseModal } = useModal();

    function createLinks(menu = [], depth = 0) {
        return menu.map((submenu, index) => (
            <CustomListItem
                key={index}
                index={index}
                classes={classes}
                icon={submenu.icon}
                name={submenu.name}
                views={submenu.views}
                layout={submenu.layout}
                path={submenu.path}
                createLinks={createLinks}
                history={history}
                depth={depth}
            />
        ));
    }

    return (
        <div
            className={`${classes.root} custom-sidebar-root`}
            style={isShadcn ? undefined : { paddingLeft: 10, paddingRight: 10 }}
        >
            <List component="nav">
                {createLinks(treeMenu)}
                <RequestFormModal open={open} handleCloseModal={handleCloseModal} />
                {user && user.isAdmin === 0 && showSubmitRequest && (
                    <ListItem
                        button
                        onClick={handleOpenModal}
                        style={{ paddingLeft: 10 }}
                        selected={false}
                    >
                        <ListItemIcon style={{ minWidth: 35 }}>
                            <MaterialSymbol style={{ color: "white" }}>send</MaterialSymbol>
                        </ListItemIcon>
                        <ListItemText primary={"Submit Request"} />
                    </ListItem>
                )}
            </List>
        </div>
    );
}
